import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu, Paperclip, Mic, ArrowUpRight, Loader2, Box, Wrench, Check, Lightbulb,
  Layers, ArrowRight, MoreVertical, Rocket, X, Pin, PinOff,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { apiService } from '../services/api';
import { NexusLogo } from '../components/brand/NexusLogo';
import { Sidebar } from '../components/shell/Sidebar';
import { CreditsButton } from '../components/credits/CreditsButton';
import { AdminAvatarMenu } from '../components/shell/AdminAvatarMenu';
import { useT, useLocale } from '../contexts/LocaleContext';

/**
 * Nexus — Home v1.1 (Feb 2026).
 *
 * Redesign per reference Image 1. Two work modes chosen by tapping a card:
 *   • Aplicaciones (Builder): creates apps and landing pages.
 *   • Taller: analyzes and repairs an existing GitHub repo.
 *
 * Mode cards NEVER create a workspace by themselves — they only switch
 * the input context and placeholder. The workspace is created only when
 * the user hits Send.
 */
type Mode = 'apps' | 'workshop';

type RecentProject = {
  id: string;
  name: string;
  kind: 'app' | 'web' | 'workshop';
  updated_at: string;
  thumbnail_url?: string | null;
};

export default function Home() {
  const nav = useNavigate();
  const t = useT();
  const { locale } = useLocale();
  const [me, setMe] = useState<{ name?: string; email?: string; role?: string } | null>(null);
  const [mode, setMode] = useState<Mode>('apps');
  const [prompt, setPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [listening, setListening] = useState(false);
  const [recent, setRecent] = useState<RecentProject[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const recognitionRef = React.useRef<any>(null);

  useEffect(() => {
    apiService.getMe().then(setMe).catch(() => setMe(null));
    // Load recent projects (best-effort, mixes workspaces + workshop sessions).
    (async () => {
      const items: RecentProject[] = [];
      try {
        const list = await apiService.listWorkspaces();
        for (const w of (list?.workspaces || []).slice(0, 6)) {
          items.push({
            id: w.id,
            name: w.name || t('home.fallbackName'),
            kind: (w.metadata?.category === 'landing' || w.metadata?.stack === 'landing_page') ? 'web' : 'app',
            updated_at: w.updated_at || w.created_at,
            thumbnail_url: w.metadata?.thumbnail_url || w.thumbnail_url || null,
          });
        }
      } catch { /* non-fatal */ }
      try {
        const ws = await apiService.listWorkshopSessions();
        for (const s of (ws?.sessions || []).slice(0, 3)) {
          items.push({
            id: s.id, name: (s.repo_url || '').split('/').pop() || t('home.fallbackRepo'),
            kind: 'workshop', updated_at: s.created_at,
          });
        }
      } catch { /* non-fatal */ }
      items.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      setRecent(items.slice(0, 6));
    })();
  }, []);

  const [homeApps, setHomeApps] = useState<{ deployed: any[]; pinned: any[] }>({ deployed: [], pinned: [] });
  const [showManagePinned, setShowManagePinned] = useState(false);

  const loadHomeApps = React.useCallback(async () => {
    try {
      const r = await apiService.getHomeApps();
      setHomeApps({ deployed: r.deployed || [], pinned: r.pinned || [] });
    } catch {
      setHomeApps({ deployed: [], pinned: [] });
    }
  }, []);

  useEffect(() => { loadHomeApps(); }, [loadHomeApps]);

  // Sprint P0 Preview Roto (Feb 2026) · Mass wake-all al montar el Home.
  // Reactivar TODOS los runtimes válidos del usuario en un solo hit
  // servidor. Sin créditos, sin rebuild. Cuando el usuario haga click
  // en cualquiera de sus proyectos, el Preview ya está listo sin
  // pantalla negra ni splash de "Preparando…".
  useEffect(() => {
    const t = setTimeout(() => {
      void apiService.runtimeWakeAll().catch(() => { /* silent */ });
    }, 800);
    return () => clearTimeout(t);
  }, []);

  // Sprint P0 v1.43 · Refresco automático del listado cuando cualquier
  // otra vista dispara un evento de publish. Así la nueva app aparece
  // en el Home sin necesidad de recargar la ventana. El segundo evento
  // "publish-thumbnail-ready" llega ~30s después con la miniatura ya
  // capturada por Playwright y provoca un segundo refresh silencioso.
  useEffect(() => {
    const handler = () => { void loadHomeApps(); };
    window.addEventListener('nexus:publish-completed', handler);
    window.addEventListener('nexus:publish-thumbnail-ready', handler);
    return () => {
      window.removeEventListener('nexus:publish-completed', handler);
      window.removeEventListener('nexus:publish-thumbnail-ready', handler);
    };
  }, [loadHomeApps]);

  const [conv, setConv] = useState<null | {
    turns: Array<{ role: 'user' | 'assistant'; content: string }>;
  }>(null);

  // Último Deploy · Smart Phase Planning state.
  const [phasePlan, setPhasePlan] = useState<null | {
    prompt: string;
    phases: Array<{ order: number; name: string; description: string }>;
    reason: string;
  }>(null);

  const startBuildFromPrompt = async (idea: string) => {
    // Second-pass helper reused after phase confirmation.
    const name = idea.split(' ').slice(0, 6).join(' ').slice(0, 60) || t('home.newProject');
    const ws = await apiService.createWorkspace({
      name, description: idea, seed_intent: idea,
    });
    for (const f of attachedFiles) {
      try { await apiService.uploadWorkspaceAttachment?.(ws.id, f); } catch { /* non-fatal */ }
    }
    nav(`/w/${ws.id}?seed=1`, { replace: true });
  };

  const confirmPhasePlan = async () => {
    if (!phasePlan) return;
    try {
      // Prefix the seed intent with the accepted phase plan so the
      // coordinator has the sequential guidance up-front.
      const phaseNotes = phasePlan.phases
        .map((p) => `Fase ${p.order}: ${p.name} — ${p.description}`)
        .join('\n');
      const merged = `${phasePlan.prompt}\n\n[Plan aceptado]\n${phaseNotes}`;
      setPhasePlan(null);
      setCreating(true);
      await startBuildFromPrompt(merged);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('home.createError'));
      setCreating(false);
    }
  };

  const submit = async () => {
    const idea = prompt.trim();
    if (!idea || creating) return;
    setCreating(true);
    try {
      if (mode === 'workshop') {
        // Workshop path: idea must be a repo URL.
        if (!/^https?:\/\/|^git@/i.test(idea)) {
          toast.error(t('home.workshopUrlInvalid'));
          setCreating(false);
          return;
        }
        const session = await apiService.createWorkshopSession(idea);
        nav(`/workshop/${session.id}`, { replace: true });
        return;
      }

      // Sprint Arquitecto IA · el primer mensaje SIEMPRE va al Arquitecto.
      // El Arquitecto decide qué hacer:
      //   • intent = "chat" | "question" → responde y NO crea workspace
      //     (el workspace se crea igualmente en el backend con status=temp
      //      y se limpiará a las 48h si el usuario no vuelve).
      //   • intent = "build" | "modify" → crea/promociona el workspace y
      //     dispara el Builder existente vía spec estructurada.
      // Sea cual sea el intent, el Arquitecto ya devuelve un workspace_id.
      let attachments: Array<{ name: string; data_url?: string; kind: string }> = [];
      if (attachedFiles.length > 0) {
        // Adjuntamos como data URLs solo para imágenes; otros archivos van
        // como referencia por nombre.
        attachments = await Promise.all(attachedFiles.map(async (f) => {
          const isImage = f.type.startsWith('image/');
          if (!isImage) return { name: f.name, kind: 'file' };
          const dataUrl = await new Promise<string>((res, rej) => {
            const rd = new FileReader();
            rd.onload = () => res(rd.result as string);
            rd.onerror = () => rej(rd.error);
            rd.readAsDataURL(f);
          });
          return { name: f.name, kind: 'image', data_url: dataUrl };
        }));
      }

      const arch = await apiService.architectChat({
        message: idea,
        attachments,
      });

      // Sprint Cierre de Base (v1.26) · TODA conversación vive dentro
      // de un Workspace. El Home siempre queda limpio como pantalla
      // de inicio. Sea cual sea el intent, navegamos al Workspace y
      // continuamos la conversación allí (el Workspace ya se creó en
      // el backend y guarda la memoria completa).
      for (const f of attachedFiles) {
        try { await apiService.uploadWorkspaceAttachment?.(arch.workspace_id, f); }
        catch { /* non-fatal */ }
      }
      // Si el Arquitecto necesita 1-2 preguntas clave, las pasamos al
      // Workspace como state inicial para mostrar como sugerencias.
      const params = new URLSearchParams();
      if (arch.trigger_builder) params.set('seed', '1');
      params.set('architect', '1');
      nav(`/w/${arch.workspace_id}?${params.toString()}`, { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('home.createError'));
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // ── Attach file ──────────────────────────────────────────────────
  const openFilePicker = () => fileInputRef.current?.click();
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    setAttachedFiles((prev) => [...prev, ...files]);
    toast.success(t('home.attachHint', { count: files.length }));
    e.target.value = '';
  };
  const removeFile = (idx: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Sprint Arquitectura Definitiva #12 · Push-to-talk mic.
  //
  // Comportamiento tipo ChatGPT: mantener presionado el botón habla,
  // soltar dispara la transcripción y auto-envía. Fallback: tap (click)
  // funciona como toggle clásico para desktop sin touch.
  const baselineRef = useRef<string>('');
  const ptt = useRef<{ startedAt: number; usedHold: boolean } | null>(null);

  const _startRecognition = (): boolean => {
    const SR: any =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { toast.error(t('home.voiceUnsupported')); return false; }
    baselineRef.current = prompt.trim();
    const rec = new SR();
    rec.lang = locale === 'en' ? 'en-US' : 'es-ES';
    rec.continuous = false;
    rec.interimResults = true;
    rec.onresult = (e: any) => {
      let full = '';
      for (let i = 0; i < e.results.length; i++) {
        full += e.results[i][0].transcript;
      }
      const base = baselineRef.current;
      setPrompt(base ? `${base} ${full.trim()}` : full.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => { setListening(false); toast.error(t('home.micError')); };
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
    return true;
  };

  const _stopRecognition = () => {
    if (listening && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      setListening(false);
    }
  };

  // Classic toggle — used by tap on desktop for accessibility.
  const toggleMic = () => {
    if (listening) { _stopRecognition(); return; }
    _startRecognition();
  };

  // Push-to-talk handlers: pointer/touch/mouse events start on press
  // and stop on release. A minimum press of 200 ms distinguishes hold
  // from tap so we don't fire the recognition twice.
  const onMicPress = (_e: React.PointerEvent) => {
    if (!listening) {
      ptt.current = { startedAt: Date.now(), usedHold: true };
      _startRecognition();
    }
  };
  const onMicRelease = () => {
    if (!ptt.current) return;
    const elapsed = Date.now() - ptt.current.startedAt;
    ptt.current = null;
    // If the press was very short (< 200ms), treat as a normal tap toggle.
    if (elapsed < 200) return;
    _stopRecognition();
  };

  const firstName =
    me?.name?.split(' ')[0] ||
    me?.email?.split('@')[0] ||
    t('home.defaultName');

  const modeMeta = mode === 'apps'
    ? { label: t('home.modeApps'), placeholder: t('home.modeAppsPlaceholder'), accentVar: 'var(--accent)' }
    : { label: t('home.modeWorkshop'), placeholder: t('home.modeWorkshopPlaceholder'), accentVar: 'var(--accent-2, var(--accent))' };

  return (
    <>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main
        className="min-h-[100dvh] w-full flex flex-col nx-circuit"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          paddingTop: 'env(safe-area-inset-top, 0)',
        }}
        data-testid="home-page"
      >
        {/* Ambient background halos */}
        <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full blur-3xl opacity-[0.08]"
               style={{ background: 'radial-gradient(closest-side, hsl(var(--accent)), transparent 70%)' }} />
        </div>

        {/* Top bar */}
        <header className="relative flex items-center justify-between px-5 sm:px-8 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <button
              data-testid="home-menu-btn"
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-white/5 transition"
              aria-label={t('home.menu')}
            >
              <Menu className="w-5 h-5" />
            </button>
            <NexusLogo size={30} showText />
          </div>
          <div className="flex items-center gap-3">
            <CreditsButton
              testId="home-credits-btn"
              returnTo={typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined}
            />
            <AdminAvatarMenu />
          </div>
        </header>

        {/* Hero */}
        <section className="relative flex-1 flex flex-col items-center max-w-3xl w-full mx-auto px-4 sm:px-6 pt-2 sm:pt-4 pb-4" style={{ gap: '0.5rem' }}>
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-2 sm:mb-4 relative w-full"
          >
            <div
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] mb-1"
              style={{ color: 'hsl(var(--accent))' }}
              data-testid="home-welcome"
            >
              {t('home.welcomeUser', { name: (me?.role === 'admin' ? 'ADMIN' : firstName.toUpperCase()) })}
            </div>
            <h1
              className="text-3xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
              style={{ color: 'hsl(var(--text))' }}
              data-testid="home-hero-title"
            >
              {t('home.titlePre')}{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--accent)), hsl(var(--accent-2, var(--accent))))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('home.titleMid')}
              </span>{' '}
              {t('home.titlePost')}
            </h1>
            <p
              className="mt-1.5 text-xs sm:text-sm leading-relaxed max-w-xl mx-auto hidden sm:block"
              style={{ color: 'hsl(var(--text-muted))' }}
            >
              {t('home.subtitle')}
            </p>
          </motion.div>

          {/* Consejo card (hidden on mobile to free vertical space).  */}
          <div
            className="w-full mb-2 nx-glass nx-halo-soft px-4 py-2 hidden sm:flex items-start gap-3"
            data-testid="home-consejo-card"
          >
            <span
              className="w-8 h-8 rounded-xl grid place-items-center shrink-0"
              style={{
                background: 'rgba(0, 240, 255, 0.10)',
                border: '1px solid rgba(0, 240, 255, 0.30)',
                color: '#00F0FF',
              }}
              aria-hidden
            >
              <Lightbulb size={15} />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#00F0FF' }}>
                {t('home.tipLabel')}
              </div>
              <div className="text-xs mt-1 leading-relaxed" style={{ color: '#9BA1A6' }}>
                {t('home.tipBody')}
              </div>
            </div>
          </div>

          {/* Mode selector — hidden on mobile since there's only one mode
              and it's shown inside the composer badge anyway. */}
          <div className="w-full mb-2 hidden sm:block">
            <ModeCard
              active={true}
              accent="hsl(var(--accent))"
              icon={<Box className="w-6 h-6" strokeWidth={1.6} />}
              title={t('home.modeApps')}
              subtitle={t('home.modeAppsHint')}
              onClick={() => { /* single mode */ }}
              testid="home-mode-card"
            />
          </div>

          {/* Prompt composer */}
          <div
            className="w-full p-4 sm:p-6 rounded-3xl relative overflow-hidden"
            style={{
              background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.92), hsl(var(--bg-elev) / 0.55))',
              border: `1px solid ${mode === 'apps' ? 'hsl(var(--accent) / 0.35)' : 'hsl(268 100% 70% / 0.35)'}`,
              boxShadow: mode === 'apps'
                ? '0 20px 60px -20px hsl(var(--accent) / 0.35), inset 0 1px 0 hsl(0 0% 100% / 0.04)'
                : '0 20px 60px -20px hsl(268 100% 70% / 0.35), inset 0 1px 0 hsl(0 0% 100% / 0.04)',
              backdropFilter: 'blur(20px)',
            }}
            data-testid="home-prompt-composer"
          >
            <div className="flex items-center gap-3 mb-4">
              <span
                className="text-[10px] uppercase tracking-[0.28em]"
                style={{ color: 'hsl(var(--text-muted))' }}
              >
                {t('home.selectedMode')}
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
                style={{
                  background: mode === 'apps' ? 'hsl(var(--accent) / 0.15)' : 'hsl(268 100% 70% / 0.15)',
                  border: mode === 'apps' ? '1px solid hsl(var(--accent) / 0.4)' : '1px solid hsl(268 100% 70% / 0.4)',
                  color: mode === 'apps' ? 'hsl(var(--accent))' : 'hsl(268 100% 78%)',
                }}
                data-testid="home-selected-mode-badge"
              >
                {modeMeta.label}
              </span>
            </div>

            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={modeMeta.placeholder}
              rows={3}
              className="w-full bg-transparent outline-none resize-none text-lg leading-relaxed placeholder:opacity-40"
              style={{ color: 'hsl(var(--text))' }}
              data-testid="home-prompt-input"
            />

            <div className="mt-4 pt-4 border-t flex items-center justify-between"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={onFileChange}
                  data-testid="home-file-input"
                />
                <ComposerAction
                  icon={<Paperclip className="w-4 h-4" />}
                  label={t('home.attach')}
                  onClick={openFilePicker}
                  testid="home-attach-btn"
                />
                <ComposerAction
                  icon={<Mic className="w-4 h-4" />}
                  label={t('home.voice')}
                  onClick={toggleMic}
                  onPress={onMicPress}
                  onRelease={onMicRelease}
                  active={listening}
                  testid="home-mic-btn"
                />
              </div>
              <button
                onClick={submit}
                disabled={!prompt.trim() || creating}
                className="w-12 h-12 rounded-full grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed transition hover:scale-[1.05] active:scale-[0.97]"
                style={{
                  background: 'linear-gradient(160deg, #00F0FF, #33F3FF)',
                  color: '#04121a',
                  boxShadow:
                    '0 0 0 1px rgba(0, 240, 255, 0.55), ' +
                    '0 0 22px rgba(0, 240, 255, 0.45), ' +
                    '0 12px 30px -10px rgba(0, 240, 255, 0.55)',
                }}
                aria-label={t('home.send')}
                data-testid="home-send-btn"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />}
              </button>
            </div>

            {attachedFiles.length > 0 && (
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2" style={{ borderColor: 'hsl(var(--border))' }}>
                {attachedFiles.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs"
                    style={{
                      background: 'hsl(var(--accent) / 0.12)',
                      border: '1px solid hsl(var(--accent) / 0.3)',
                      color: 'hsl(var(--accent))',
                    }}
                    data-testid={`home-attached-${i}`}
                  >
                    <Paperclip className="w-3 h-3" />
                    {f.name}
                    <button onClick={() => removeFile(i)} aria-label={t('home.removeAttach')} className="ml-1 opacity-70 hover:opacity-100">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Conversational reply panel — shown when the intent gate
              detected a chat message instead of a build order. */}
          {conv && conv.turns.length > 0 && (
            <div
              className="w-full mt-6 rounded-2xl overflow-hidden"
              style={{
                background: 'hsl(var(--bg-elev) / 0.75)',
                border: '1px solid hsl(var(--accent) / 0.25)',
                backdropFilter: 'blur(14px)',
              }}
              data-testid="home-conversational-panel"
            >
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: '1px solid hsl(var(--border))' }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full grid place-items-center"
                    style={{ background: 'hsl(var(--accent) / 0.18)', color: 'hsl(var(--accent))' }}
                  >
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                  <span
                    className="text-[11px] uppercase tracking-[0.24em] font-medium"
                    style={{ color: 'hsl(var(--accent))' }}
                  >
                    Nexus IA · Modo conversación
                  </span>
                </div>
                <button
                  onClick={() => setConv(null)}
                  className="text-[11px]"
                  style={{ color: 'hsl(var(--text-muted))' }}
                  data-testid="home-conv-clear"
                >
                  Limpiar
                </button>
              </div>
              <div className="p-4 space-y-3 max-h-[420px] overflow-y-auto">
                {conv.turns.map((tt, i) => (
                  <div key={i} className={tt.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                    <div
                      className="max-w-[90%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed"
                      style={
                        tt.role === 'user'
                          ? { background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.3)', color: 'hsl(var(--text))' }
                          : { background: 'hsl(var(--bg) / 0.55)', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text))' }
                      }
                      data-testid={`home-conv-turn-${tt.role}-${i}`}
                    >
                      {tt.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sprint Arquitectura Definitiva · Apps desplegadas.
              Solo aplicaciones con Deploy real. Nunca Preview ni drafts.
              El grid siempre muestra 4 tarjetas: pinned reales + placeholders
              que se auto-rellenan según despliegues. */}
          <DeployedAppsGrid
            deployed={homeApps.deployed}
            pinned={homeApps.pinned}
            onManage={() => setShowManagePinned(true)}
            onOpen={(url) => window.open(url, '_blank', 'noopener')}
          />

          {/* Recent projects (Workspaces en construcción) — visible sólo si
              hay proyectos NO desplegados (drafts / previews). */}
          {recent.length > 0 && (
            <div className="w-full mt-6 sm:mt-10" data-testid="home-recent-section">
              <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-lg grid place-items-center"
                    style={{
                      background: 'hsl(var(--accent) / 0.14)',
                      border: '1px solid hsl(var(--accent) / 0.3)',
                    }}
                  >
                    <Layers className="w-4 h-4" style={{ color: 'hsl(var(--accent))' }} />
                  </div>
                  <span
                    className="text-[11px] uppercase tracking-[0.28em] font-medium"
                    style={{ color: 'hsl(var(--text))' }}
                  >
                    {t('home.recentTitle')}
                  </span>
                </div>
                <button
                  onClick={() => nav('/dashboard')}
                  className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  style={{ color: 'hsl(var(--accent))' }}
                  data-testid="home-recent-see-all"
                >
                  {t('home.recentSeeAll')} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(typeof window !== 'undefined' && window.innerWidth < 640 ? recent.slice(0, 1) : recent).map((p) => (
                  <RecentCard key={p.id} project={p} onOpen={() => {
                    if (p.kind === 'workshop') nav(`/workshop/${p.id}`);
                    else nav(`/w/${p.id}`);
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* Reference image 1 · bottom hint strip. Hidden on mobile to
              guarantee no scroll on iPhone-sized viewports. */}
          <div
            className="w-full mt-6 sm:mt-10 rounded-2xl px-4 py-3 hidden sm:flex items-center gap-2.5"
            style={{
              background: 'hsl(var(--accent) / 0.05)',
              border: '1px solid hsl(var(--accent) / 0.20)',
            }}
            data-testid="home-tip"
          >
            <span
              className="w-6 h-6 rounded-full grid place-items-center shrink-0"
              style={{ background: 'hsl(var(--accent) / 0.20)', color: 'hsl(var(--accent))' }}
            >✦</span>
            <div className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
              <span style={{ color: 'hsl(var(--accent))', fontWeight: 500 }}>{t('home.tipLabel')}:</span>{' '}
              {t('home.tipText')}
            </div>
          </div>
        </section>
      </main>

      {/* Último Deploy · Smart Phase Planning confirmation modal */}
      {phasePlan && (
        <div
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPhasePlan(null)}
          data-testid="home-phase-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[520px] max-w-full max-h-[90dvh] overflow-y-auto rounded-2xl p-6 border"
            style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--accent) / 0.35)' }}
          >
            <div className="text-xs uppercase tracking-widest font-semibold mb-1"
                 style={{ color: 'hsl(var(--accent))' }}>
              Plan sugerido
            </div>
            <div className="text-lg font-medium mb-2" style={{ color: 'hsl(var(--text))' }}>
              Detectamos varias piezas grandes
            </div>
            <div className="text-xs mb-4 leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
              {phasePlan.reason}
            </div>
            <ol className="space-y-2">
              {phasePlan.phases.map((p) => (
                <li key={p.order}
                    className="rounded-xl p-3"
                    style={{
                      background: 'hsl(var(--bg) / 0.6)',
                      border: '1px solid hsl(var(--border))',
                    }}
                    data-testid={`home-phase-item-${p.order}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-semibold"
                          style={{ background: 'hsl(var(--accent) / 0.2)', color: 'hsl(var(--accent))' }}>
                      {p.order}
                    </span>
                    <span className="text-sm font-medium" style={{ color: 'hsl(var(--text))' }}>{p.name}</span>
                  </div>
                  <div className="text-xs leading-relaxed pl-7" style={{ color: 'hsl(var(--text-muted))' }}>
                    {p.description}
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setPhasePlan(null)}
                className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/5"
                style={{ color: 'hsl(var(--text-muted))' }}
                data-testid="home-phase-cancel"
              >
                Editar prompt
              </button>
              <button
                onClick={confirmPhasePlan}
                className="px-4 py-1.5 rounded-lg text-sm font-medium"
                style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
                data-testid="home-phase-confirm"
              >
                Empezar fase 1
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sprint Arquitectura Definitiva · Gestionar apps destacadas */}
      {showManagePinned && (
        <div
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowManagePinned(false)}
          data-testid="home-manage-pinned-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[560px] max-w-full max-h-[90dvh] overflow-y-auto rounded-2xl p-6 border"
            style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--border))' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'hsl(var(--accent))' }}>
                  {t('home.deployedManageTitle')}
                </div>
                <div className="text-sm mt-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
                  {t('home.deployedManageHint')}
                </div>
              </div>
              <button
                onClick={() => setShowManagePinned(false)}
                className="w-8 h-8 rounded-full grid place-items-center"
                style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--text-muted))' }}
                data-testid="home-manage-pinned-close"
                aria-label={t('home.close')}
              >
                <X size={12} />
              </button>
            </div>
            {homeApps.deployed.length === 0 ? (
              <div className="text-sm text-center py-8" style={{ color: 'hsl(var(--text-muted))' }}>
                {t('home.deployedEmpty')}
              </div>
            ) : (
              <ManagePinnedList
                deployed={homeApps.deployed}
                pinned={homeApps.pinned}
                onSave={async (ids) => {
                  try {
                    await apiService.setPinnedApps(ids);
                    await loadHomeApps();
                    setShowManagePinned(false);
                    toast.success(t('home.deployedSaved'));
                  } catch (e: any) {
                    toast.error(e?.response?.data?.detail || t('home.deployedSaveError'));
                  }
                }}
                t={t}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

const ModeCard: React.FC<{
  active: boolean;
  accent: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  testid: string;
}> = ({ active, accent, icon, title, subtitle, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    data-active={active ? 'true' : 'false'}
    className="relative w-full text-left rounded-3xl p-5 sm:p-6 transition group"
    style={{
      background: active
        ? `linear-gradient(135deg, hsl(var(--bg-elev) / 0.95), hsl(var(--bg-elev) / 0.6))`
        : `hsl(var(--bg-elev) / 0.5)`,
      border: `1px solid ${active ? accent : 'hsl(var(--border))'}`,
      boxShadow: active
        ? `0 20px 60px -20px ${accent.replace(')', ' / 0.35)')}, inset 0 1px 0 hsl(0 0% 100% / 0.04)`
        : `inset 0 1px 0 hsl(0 0% 100% / 0.03)`,
      backdropFilter: 'blur(16px)',
    }}
  >
    {active && (
      <span
        className="absolute top-4 right-4 w-6 h-6 rounded-full grid place-items-center"
        style={{ background: accent, boxShadow: `0 4px 18px ${accent.replace(')', ' / 0.45)')}` }}
      >
        <Check className="w-3.5 h-3.5" style={{ color: '#04141a' }} strokeWidth={3} />
      </span>
    )}
    <div className="flex items-center gap-4">
      <div
        className="w-14 h-14 rounded-2xl grid place-items-center shrink-0"
        style={{
          background: `${accent.replace(')', ' / 0.15)')}`,
          border: `1px solid ${accent.replace(')', ' / 0.4)')}`,
          color: accent,
          boxShadow: active ? `inset 0 0 24px ${accent.replace(')', ' / 0.25)')}` : 'none',
        }}
      >
        {icon}
      </div>
      <div className="min-w-0 pr-8">
        <div className="text-lg font-medium tracking-tight" style={{ color: 'hsl(var(--text))' }}>{title}</div>
        <div className="text-xs sm:text-sm mt-1" style={{ color: 'hsl(var(--text-muted))' }}>{subtitle}</div>
      </div>
    </div>
  </button>
);

const ComposerAction: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  testid: string;
  onPress?: (e: React.PointerEvent) => void;
  onRelease?: () => void;
}> = ({ icon, label, onClick, active, testid, onPress, onRelease }) => (
  <button
    type="button"
    onClick={onClick}
    onPointerDown={onPress}
    onPointerUp={onRelease}
    onPointerLeave={onRelease}
    onPointerCancel={onRelease}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-sm transition select-none"
    style={{
      color: active ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))',
    }}
  >
    {icon}
    <span className="hidden sm:inline">{label}</span>
    {active && <span className="text-[9px] uppercase tracking-widest opacity-70">Rec…</span>}
  </button>
);

// ── Manage pinned apps modal helper ────────────────────────────────
const ManagePinnedList: React.FC<{
  deployed: any[]; pinned: any[];
  onSave: (ids: string[]) => Promise<void>;
  t: (k: string, v?: any) => string;
}> = ({ deployed, pinned, onSave, t }) => {
  const [ids, setIds] = useState<string[]>(pinned.map((p) => p.workspace_id));
  const isPinned = (id: string) => ids.includes(id);
  const toggle = (id: string) => {
    setIds((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 4) {
        toast.error(t('home.deployedMaxFour'));
        return cur;
      }
      return [...cur, id];
    });
  };
  const move = (id: string, dir: -1 | 1) => {
    setIds((cur) => {
      const i = cur.indexOf(id); if (i < 0) return cur;
      const j = i + dir; if (j < 0 || j >= cur.length) return cur;
      const next = cur.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  return (
    <div>
      {/* Currently pinned in order */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: 'hsl(var(--accent))' }}>
          {t('home.deployedPinnedList', { count: ids.length })}
        </div>
        <ul className="space-y-2">
          {ids.length === 0 && (
            <li className="text-xs italic" style={{ color: 'hsl(var(--text-muted))' }}>
              {t('home.deployedPinnedEmpty')}
            </li>
          )}
          {ids.map((id, i) => {
            const app = deployed.find((d) => d.workspace_id === id);
            if (!app) return null;
            return (
              <li key={id} className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                  style={{ background: 'hsl(var(--bg) / 0.6)', border: '1px solid hsl(var(--border))' }}>
                <span className="text-xs w-5 text-center font-semibold" style={{ color: 'hsl(var(--accent))' }}>{i + 1}</span>
                <span className="text-sm truncate flex-1">{app.workspace_name}</span>
                <button onClick={() => move(id, -1)} disabled={i === 0}
                        className="w-6 h-6 grid place-items-center rounded-md disabled:opacity-30"
                        style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text-muted))' }}
                        data-testid={`home-manage-up-${id}`}>↑</button>
                <button onClick={() => move(id, 1)} disabled={i === ids.length - 1}
                        className="w-6 h-6 grid place-items-center rounded-md disabled:opacity-30"
                        style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(var(--text-muted))' }}
                        data-testid={`home-manage-down-${id}`}>↓</button>
                <button onClick={() => toggle(id)}
                        className="w-6 h-6 grid place-items-center rounded-md"
                        style={{ background: 'hsl(var(--bg-elev))', color: 'hsl(0 70% 60%)' }}
                        data-testid={`home-manage-unpin-${id}`}>
                  <PinOff size={11} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: 'hsl(var(--text-muted))' }}>
        {t('home.deployedAllApps')}
      </div>
      <ul className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
        {deployed.map((app) => {
          const pin = isPinned(app.workspace_id);
          return (
            <li key={app.workspace_id}>
              <button
                onClick={() => toggle(app.workspace_id)}
                className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left"
                style={{
                  background: pin ? 'hsl(var(--accent) / 0.08)' : 'transparent',
                  border: `1px solid ${pin ? 'hsl(var(--accent) / 0.3)' : 'hsl(var(--border))'}`,
                }}
                data-testid={`home-manage-toggle-${app.workspace_id}`}
              >
                <Pin size={11} style={{ color: pin ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }} />
                <span className="text-sm truncate flex-1" style={{ color: 'hsl(var(--text))' }}>{app.workspace_name}</span>
                <span className="text-[10px] uppercase tracking-widest"
                      style={{ color: pin ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }}>
                  {pin ? t('home.pinned') : t('home.pin')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onSave(ids)}
          className="px-4 py-1.5 rounded-lg text-sm font-medium"
          style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
          data-testid="home-manage-save"
        >
          {t('home.deployedSaveBtn')}
        </button>
      </div>
    </div>
  );
};

// ── Sprint Arquitectura Definitiva · Apps desplegadas ──────────────
//
// Renders a 4-slot grid. Each slot is either:
//   • A pinned app if the user selected one.
//   • The next most-recent deployed app that isn't already pinned.
//   • A modern placeholder card when nothing else is available.
//
// The user can open "Gestionar destacadas" to reorder + select up to 4.
const DeployedAppsGrid: React.FC<{
  deployed: any[];
  pinned: any[];
  onManage: () => void;
  onOpen: (url: string) => void;
}> = ({ deployed, pinned, onManage, onOpen }) => {
  const t = useT();
  // Fill 4 slots: pinned first, then most-recent deployed apps not pinned.
  const pinnedIds = new Set(pinned.map((p) => p.workspace_id));
  const fillers = deployed.filter((d) => !pinnedIds.has(d.workspace_id)).slice(0, 4);
  const slots: Array<any | null> = [...pinned, ...fillers].slice(0, 4);
  while (slots.length < 4) slots.push(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="w-full mt-6 sm:mt-10" data-testid="home-deployed-section">
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg grid place-items-center"
            style={{ background: 'hsl(var(--accent) / 0.14)', border: '1px solid hsl(var(--accent) / 0.3)' }}
          >
            <Rocket className="w-4 h-4" style={{ color: 'hsl(var(--accent))' }} />
          </div>
          <span
            className="text-[11px] uppercase tracking-[0.28em] font-medium"
            style={{ color: 'hsl(var(--text))' }}
          >
            {t('home.deployedTitle')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {deployed.length > 0 && (
            <button
              onClick={onManage}
              className="text-xs font-medium hover:underline"
              style={{ color: 'hsl(var(--accent))' }}
              data-testid="home-deployed-manage"
            >
              {t('home.deployedManage')}
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {slots.map((app, idx) =>
          app ? (
            <DeployedCard key={app.workspace_id} app={app} onOpen={() => app.url && onOpen(app.url)} backendUrl={backendUrl} />
          ) : (
            <PlaceholderCard key={`ph-${idx}`} index={idx} />
          )
        )}
      </div>
    </div>
  );
};

const DeployedCard: React.FC<{ app: any; onOpen: () => void; backendUrl: string }> = ({ app, onOpen, backendUrl }) => {
  const t = useT();
  // Sprint P0 v1.43 · miniatura REAL capturada por Playwright vs el
  // fallback genérico (deployments/{ws}/thumbnail). Preferimos siempre
  // la real si ya está persistida. Como Playwright puede tardar ~5-25s
  // tras publish, la miniatura real llegará en el siguiente refetch
  // que dispara el evento `nexus:publish-thumbnail-ready`.
  const realThumb: string | null = app.thumbnail_url || null;
  const resolvedThumb = realThumb
    ? (realThumb.startsWith('http') ? realThumb : `${backendUrl}${realThumb}`)
    : `${backendUrl}/api/deployments/${app.workspace_id}/thumbnail`;
  const kindLabel =
    app.category === 'landing' || app.stack === 'landing_page' ? t('home.tagWeb') :
    app.category === 'ecommerce' ? 'E-commerce' :
    app.category === 'dashboard' ? 'Dashboard' :
    t('home.tagApp');
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative text-left rounded-2xl overflow-hidden transition group hover:-translate-y-0.5"
      style={{
        background: 'hsl(var(--bg-elev) / 0.7)',
        border: '1px solid hsl(var(--border))',
        aspectRatio: '4 / 3',
      }}
      data-testid={`home-deployed-${app.workspace_id}`}
      title={app.workspace_name}
    >
      <img
        src={resolvedThumb}
        alt={app.workspace_name}
        className="w-full h-full object-cover"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.2'; }}
      />
      <div
        className="absolute inset-x-0 bottom-0 p-3"
        style={{ background: 'linear-gradient(0deg, hsl(var(--bg) / 0.95) 0%, transparent 100%)' }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-semibold"
            style={{ background: 'hsl(150 60% 50% / 0.18)', color: 'hsl(150 65% 65%)', border: '1px solid hsl(150 60% 50% / 0.4)' }}
          >
            En línea
          </span>
          <span
            className="text-[9px] uppercase tracking-widest"
            style={{ color: 'hsl(var(--text-muted))' }}
          >
            {kindLabel}
          </span>
        </div>
        <div className="text-sm font-medium truncate" style={{ color: 'hsl(var(--text))' }}>
          {app.workspace_name}
        </div>
      </div>
    </button>
  );
};

const PlaceholderCard: React.FC<{ index: number }> = ({ index }) => {
  const t = useT();
  // Sprint Corrección Crítica · Mockups premium de apps de ejemplo.
  // Cada placeholder representa un tipo de app moderna con vista previa
  // detallada — ya no se ve como wireframe genérico sino como una
  // captura real y atractiva de una app profesional.
  const mocks: Array<{ title: string; hint: string; accent: string; sketch: React.ReactNode }> = [
    {
      title: t('home.mockDashboardTitle'),
      hint: t('home.mockDashboardHint'),
      accent: '#00F0FF',
      sketch: (
        <div className="w-full h-full flex flex-col p-2.5" style={{ background: 'linear-gradient(135deg, #0a1424 0%, #061018 100%)' }}>
          {/* Top bar */}
          <div className="flex items-center gap-1 mb-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#00F0FF' }} />
            <div className="h-1.5 rounded flex-1" style={{ background: 'rgba(0,240,255,0.35)' }} />
            <div className="h-1.5 w-3 rounded" style={{ background: 'rgba(0,240,255,0.6)' }} />
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-1 mb-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded p-1" style={{ background: 'rgba(0,240,255,0.08)', border: '0.5px solid rgba(0,240,255,0.2)' }}>
                <div className="h-0.5 w-2 rounded mb-0.5" style={{ background: 'rgba(0,240,255,0.5)' }} />
                <div className="h-1.5 rounded" style={{ background: 'rgba(0,240,255,0.7)' }} />
              </div>
            ))}
          </div>
          {/* Chart */}
          <div className="flex-1 rounded relative overflow-hidden" style={{ background: 'rgba(0,240,255,0.05)', border: '0.5px solid rgba(0,240,255,0.15)' }}>
            <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full">
              <path d="M0,30 L20,25 L40,15 L60,20 L80,8 L100,12" fill="none" stroke="#00F0FF" strokeWidth="1.5" opacity="0.9" />
              <path d="M0,30 L20,25 L40,15 L60,20 L80,8 L100,12 L100,40 L0,40 Z" fill="url(#dashGrad)" opacity="0.3" />
              <defs>
                <linearGradient id="dashGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      ),
    },
    {
      title: t('home.mockShopTitle'),
      hint: t('home.mockShopHint'),
      accent: '#c084fc',
      sketch: (
        <div className="w-full h-full flex flex-col p-2.5" style={{ background: 'linear-gradient(135deg, #0f0820 0%, #06031a 100%)' }}>
          {/* Header con logo */}
          <div className="flex items-center justify-between mb-2">
            <div className="h-1.5 w-4 rounded" style={{ background: 'rgba(192,132,252,0.7)' }} />
            <div className="flex gap-0.5">
              <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(192,132,252,0.5)' }} />
              <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(192,132,252,0.5)' }} />
            </div>
          </div>
          {/* Grid productos */}
          <div className="grid grid-cols-2 gap-1 flex-1">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded flex flex-col overflow-hidden" style={{ background: 'rgba(192,132,252,0.1)', border: '0.5px solid rgba(192,132,252,0.25)' }}>
                <div className="flex-1" style={{ background: `radial-gradient(circle at ${30 + i * 15}% 50%, rgba(192,132,252,0.3), transparent 70%)` }} />
                <div className="px-1 py-0.5">
                  <div className="h-0.5 w-3/4 rounded" style={{ background: 'rgba(192,132,252,0.6)' }} />
                  <div className="h-0.5 w-1/3 rounded mt-0.5" style={{ background: 'rgba(192,132,252,0.9)' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: t('home.mockLandingTitle'),
      hint: t('home.mockLandingHint'),
      accent: '#fbbf24',
      sketch: (
        <div className="w-full h-full flex flex-col items-center justify-center p-2.5 text-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #201408 0%, #0a0703 100%)' }}>
          {/* Ambient orb */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(251,191,36,0.35), transparent)', filter: 'blur(8px)' }} />
          {/* Hero */}
          <div className="relative z-10 flex flex-col items-center gap-1">
            <div className="h-1 w-3 rounded" style={{ background: 'rgba(251,191,36,0.5)' }} />
            <div className="h-2 w-14 rounded" style={{ background: 'rgba(251,191,36,0.9)' }} />
            <div className="h-1 w-10 rounded" style={{ background: 'rgba(251,191,36,0.4)' }} />
            <div className="h-0.5 w-8 rounded mt-0.5" style={{ background: 'rgba(255,255,255,0.15)' }} />
            <div className="h-2 w-6 rounded-full mt-1" style={{ background: '#fbbf24', boxShadow: '0 0 8px rgba(251,191,36,0.6)' }} />
          </div>
        </div>
      ),
    },
    {
      title: t('home.mockChatTitle'),
      hint: t('home.mockChatHint'),
      accent: '#4ade80',
      sketch: (
        <div className="w-full h-full flex flex-col p-2.5 gap-1" style={{ background: 'linear-gradient(135deg, #061a0e 0%, #030a06 100%)' }}>
          {/* Header con avatar */}
          <div className="flex items-center gap-1 mb-1 pb-1" style={{ borderBottom: '0.5px solid rgba(74,222,128,0.15)' }}>
            <div className="w-2 h-2 rounded-full" style={{ background: 'rgba(74,222,128,0.6)' }} />
            <div className="h-1 rounded flex-1" style={{ background: 'rgba(74,222,128,0.35)' }} />
          </div>
          {/* Chat bubbles */}
          <div className="rounded-lg px-1.5 py-1 self-start max-w-[70%]" style={{ background: 'rgba(74,222,128,0.15)', border: '0.5px solid rgba(74,222,128,0.25)' }}>
            <div className="h-0.5 w-8 rounded" style={{ background: 'rgba(255,255,255,0.4)' }} />
            <div className="h-0.5 w-6 rounded mt-0.5" style={{ background: 'rgba(255,255,255,0.3)' }} />
          </div>
          <div className="rounded-lg px-1.5 py-1 self-end max-w-[70%]" style={{ background: 'rgba(74,222,128,0.35)' }}>
            <div className="h-0.5 w-6 rounded" style={{ background: '#04141a' }} />
          </div>
          <div className="rounded-lg px-1.5 py-1 self-start max-w-[75%]" style={{ background: 'rgba(74,222,128,0.15)', border: '0.5px solid rgba(74,222,128,0.25)' }}>
            <div className="h-0.5 w-10 rounded" style={{ background: 'rgba(255,255,255,0.4)' }} />
            <div className="h-0.5 w-8 rounded mt-0.5" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <div className="h-0.5 w-6 rounded mt-0.5" style={{ background: 'rgba(255,255,255,0.3)' }} />
          </div>
          {/* Input pill */}
          <div className="mt-auto flex items-center gap-1 rounded-full px-1.5 py-0.5" style={{ background: 'rgba(74,222,128,0.08)', border: '0.5px solid rgba(74,222,128,0.25)' }}>
            <div className="h-0.5 rounded flex-1" style={{ background: 'rgba(255,255,255,0.15)' }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4ade80' }} />
          </div>
        </div>
      ),
    },
  ];
  const mock = mocks[index % 4];
  return (
    <div
      className="relative rounded-2xl overflow-hidden group transition hover:-translate-y-0.5"
      style={{
        background: `linear-gradient(145deg, ${mock.accent}22, transparent 85%)`,
        border: `1px solid ${mock.accent}44`,
        aspectRatio: '4 / 3',
        boxShadow: `0 8px 32px -12px ${mock.accent}33`,
      }}
      data-testid={`home-deployed-placeholder-${index}`}
      title={mock.title}
    >
      {/* Preview: ocupa toda la card, se ve como una captura real */}
      <div className="absolute inset-0">{mock.sketch}</div>
      {/* Overlay inferior con label + hint */}
      <div
        className="absolute inset-x-0 bottom-0 p-2.5"
        style={{ background: 'linear-gradient(0deg, rgba(3,4,7,0.95) 0%, rgba(3,4,7,0.7) 60%, transparent 100%)' }}
      >
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] uppercase tracking-widest font-bold"
                style={{ background: `${mock.accent}22`, color: mock.accent, border: `0.5px solid ${mock.accent}55` }}>
            Ejemplo
          </span>
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'hsl(var(--text))' }}>
            {mock.title}
          </span>
        </div>
        <div className="text-[9px] mt-0.5 leading-tight truncate" style={{ color: 'hsl(var(--text-muted))' }}>
          {mock.hint}
        </div>
      </div>
      {/* Corner shine para look premium */}
      <div className="absolute top-0 left-0 w-1/3 h-1/3 pointer-events-none opacity-40"
           style={{ background: `radial-gradient(circle at top left, ${mock.accent}66, transparent 70%)` }} />
    </div>
  );
};

const RecentCard: React.FC<{
  project: RecentProject;
  onOpen: () => void;
}> = ({ project, onOpen }) => {
  const t = useT();
  const tagColor =
    project.kind === 'app' ? 'hsl(var(--accent))' :
    project.kind === 'web' ? 'hsl(268 100% 78%)' :
    'hsl(38 92% 58%)';
  const tagLabel =
    project.kind === 'app' ? t('home.tagApp') :
    project.kind === 'web' ? t('home.tagWeb') :
    t('home.tagWorkshop');
  const timeAgo = formatTimeAgo(project.updated_at, t);
  const initial = (project.name || 'N').charAt(0).toUpperCase();
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`home-recent-${project.id}`}
      className="relative text-left rounded-3xl overflow-hidden transition group hover:-translate-y-0.5"
      style={{
        background: 'linear-gradient(160deg, hsl(var(--bg-elev) / 0.7), hsl(var(--bg-elev) / 0.35))',
        border: '1px solid hsl(var(--border))',
        backdropFilter: 'blur(14px)',
      }}
    >
      {/* Thumbnail area (reference image 1: recent projects show a small preview) */}
      <div
        className="relative aspect-[16/10] w-full overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${tagColor.replace(')', ' / 0.20)')}, hsl(var(--bg-elev) / 0.4))`,
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        {project.thumbnail_url ? (
          <img
            src={project.thumbnail_url.startsWith('http') ? project.thumbnail_url : ((process.env.REACT_APP_BACKEND_URL || '') + project.thumbnail_url)}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div
              className="text-4xl font-light"
              style={{
                color: tagColor,
                textShadow: `0 0 24px ${tagColor.replace(')', ' / 0.5)')}`,
                fontFamily: 'monospace',
              }}
            >
              {initial}
            </div>
          </div>
        )}
        <span
          className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-semibold backdrop-blur"
          style={{
            color: tagColor,
            background: 'hsl(var(--bg) / 0.65)',
            border: `1px solid ${tagColor.replace(')', ' / 0.35)')}`,
          }}
        >
          {tagLabel}
        </span>
      </div>
      <div className="p-3.5 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[14px] font-medium tracking-tight truncate" style={{ color: 'hsl(var(--text))' }}>
            {project.name}
          </div>
          <div className="text-[11px] mt-1" style={{ color: 'hsl(var(--text-muted))' }}>
            {t('home.updated')} {timeAgo}
          </div>
        </div>
        <div className="opacity-30 group-hover:opacity-80 transition shrink-0" style={{ color: 'hsl(var(--text-muted))' }}>
          <MoreVertical className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
};

function formatTimeAgo(iso: string | undefined, t: (k: string, vars?: any) => string): string {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return t('home.timeNow');
    const mins = Math.floor(secs / 60);
    if (mins < 60) return t('home.timeMinutes', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('home.timeHours', { count: hrs });
    const days = Math.floor(hrs / 24);
    return t('home.timeDays', { count: days });
  } catch {
    return '';
  }
}
